/** A record time axis's mark vocabulary (DESIGN.md §5, "Time axis") — the booking's
 *  Communication lane draws it, and every other record axis reuses it so a mark means the
 *  same thing everywhere:
 *
 *  - **A shape per kind**, so a lane is never told by colour alone: import = 10 px rounded
 *    square in navy 55%; change = filled navy diamond; audit = outlined diamond; delivery
 *    = filled navy dot; planned = outlined dot; booked = the 3×14 px navy tick; a trip's
 *    start / end = outlined handles.
 *  - **A tone in colour and in words** (the mark's text says it too): error (red), replan
 *    (amber dashed, 12% fill), done (navy 35% fill), dropped (disabled grey — dashed on a planned
 *    notification that was skipped, the fill of a sandbox's captured delivery: nothing sent).
 *  - **The "now" rule** — 1 px dashed navy; **missing data is hatched** — 8% ink diagonal,
 *    information, not ornament. */
import { alpha, type Theme } from '@mui/material';

export type TimeMarkKind = 'import' | 'change' | 'audit' | 'delivery' | 'planned' | 'booked' | 'start' | 'end';

export type TimeMarkTone = 'default' | 'error' | 'replan' | 'done' | 'dropped';

/** An `sx` callback (MUI `SxProps` accepts `(theme) => style`). */
export type MarkVisual = (t: Theme) => Record<string, unknown>;

/** The mark of one item: its kind's shape in its tone. */
export function timeMarkSx(kind: TimeMarkKind, tone: TimeMarkTone = 'default'): MarkVisual {
  return (t) => {
    const navy = t.palette.primary.main;
    const error = t.palette.error.main;
    const amber = t.palette.warning.main;
    const base = { width: 10, height: 10, boxSizing: 'border-box', display: 'block' };
    switch (kind) {
      case 'import':
        return { ...base, borderRadius: '2px', bgcolor: alpha(navy, 0.55) };
      case 'change':
        return { ...base, width: 9, height: 9, transform: 'rotate(45deg)', bgcolor: navy };
      case 'audit':
        return { ...base, width: 9, height: 9, transform: 'rotate(45deg)', border: `2px solid ${tone === 'error' ? error : navy}`, bgcolor: t.palette.background.paper };
      case 'delivery':
        // dropped: a sandbox's captured delivery — nothing was sent, as expected. Still the
        // filled dot (a tone changes the colour, never the shape), in disabled grey.
        return {
          ...base,
          borderRadius: '50%',
          bgcolor: tone === 'error' ? error : tone === 'dropped' ? t.palette.text.disabled : navy,
        };
      case 'planned':
        if (tone === 'replan') return { ...base, borderRadius: '50%', border: `2px dashed ${amber}`, bgcolor: alpha(amber, 0.12) };
        if (tone === 'done') return { ...base, borderRadius: '50%', border: `2px solid ${navy}`, bgcolor: alpha(navy, 0.35) };
        if (tone === 'dropped') return { ...base, borderRadius: '50%', border: `2px dashed ${t.palette.text.disabled}` };
        if (tone === 'error') return { ...base, borderRadius: '50%', border: `2px solid ${error}`, bgcolor: t.palette.background.paper };
        return { ...base, borderRadius: '50%', border: `2px solid ${navy}`, bgcolor: t.palette.background.paper };
      case 'booked':
        return { width: 3, height: 14, borderRadius: '1px', bgcolor: navy, display: 'block' };
      default:
        // The trip's start / end: handles on the trip bar.
        return { ...base, borderRadius: '50%', border: `2px solid ${navy}`, bgcolor: t.palette.background.paper };
    }
  };
}

/** The "now" line across an axis's lanes (position it with `left: axisPct(now)`). */
export const NOW_RULE_SX = { borderLeft: '1px dashed', borderColor: 'primary.main' } as const;

/** The hatching of a range an axis could not load (`Load earlier`). */
export function hatchSx(t: Theme): { backgroundImage: string } {
  return {
    backgroundImage: `repeating-linear-gradient(135deg, ${alpha(t.palette.text.primary, 0.08)} 0 4px, transparent 4px 8px)`,
  };
}
